<script setup>
import { onMounted, ref, computed } from "vue";
import { Icon, Button, ProfilePicture } from "@components";
import Menu from '@components/Menu.vue';
import MenuItem from './MenuItem.vue';

import { isSm, isMd } from '@utils/mediaQuery';

import { useGeneral } from '@store/general';
const { sidebarOptions, changeSidebarWidth, collapseSideMenu } = useGeneral();

// Profile store.
const { name, profile_image_url, email } = prebook_admin.profile;
const { logo_url, full_logo_url } = prebook_admin.settings.business || {};

// Return to WordPress.
const returnToWP = prebook_admin.url.admin;

// logout prompt.
const logout = async () => {
    const choice = await Prompt.fire({
        title: 'Are you sure?',
        message: 'You won\'t be able to revert this!',
        ok: 'Logout',
        cancel: 'Dismiss',
    });

    if (!choice.ok) return;

    const current_url = window.location.href;

    window.location.href = prebook_admin.url.logout + '&redirect_to=' + encodeURIComponent(current_url);
};

const changeSidebarStatus = () => {
    if (isSm || isMd) {
        sidebarOptions.sidebarCollapse = true;
        changeSidebarWidth();
    }
};

onMounted(() => {
    changeSidebarStatus();
});

window.addEventListener('resize', () => {
    changeSidebarWidth();
});

import { useRouter } from 'vue-router';
const router = useRouter();

router.beforeEach((to, from, next) => {
    changeSidebarStatus();
    next();
});

const goToHome = () => {
    router.push('/');
};

const isStaff = prebook_admin.profile.role === 'staff';

import { useSettings } from '@store/settings';
const $settings = useSettings();

const sidebarLogoGroupHeight = ref(0);

const menuStyle = computed(() => {
    return {
        height: `calc(100% - 254px - ${sidebarLogoGroupHeight.value}px)`,
        overflow: 'auto'
    };
});

onMounted(() => {
    setTimeout(() => {
        sidebarLogoGroupHeight.value = document.getElementById('sidebarLogoGroup').clientHeight;
    }, 1500);
});
</script>

<template>
<div class="fixed overflow-hidden bg-white top-0 z-50 h-screen border-[#EAECF0]"
    :class="[$settings.isRTL ? 'right-0 border-l' : 'left-0 border-r']" :style="`width:${sidebarOptions.sidebarWidth}`">
    <!-- sidebar logo -->
    <div id="sidebarLogoGroup" class="cursor-pointer flex items-center gap-2.5 pt-8 pb-6 pl-6 font-bold lg:pr-5">
        <Icon @click="goToHome()" v-if="sidebarOptions.sidebarCollapse && !logo_url" name="logomark" size="32px" />
        <img @click="goToHome()" v-if="sidebarOptions.sidebarCollapse && logo_url" :src="logo_url" width="32px"
            height="32px" class="w-[32px] h-[32px]" />

        <Icon @click="goToHome()" v-if="!sidebarOptions.sidebarCollapse && !full_logo_url" name="logoLight" width="60px"
            height="auto" />
        <img @click="goToHome()" v-if="!sidebarOptions.sidebarCollapse && full_logo_url" :src="full_logo_url"
            width="110px" height="22px" />

        <Icon v-if="isSm || isMd" @click="collapseSideMenu()" name="chevronLeftDouble" size="20px"
            :class="`${sidebarOptions.sidebarCollapse == true ? 'rotate-180' : ''} transition-all duration-500 text-[#667085] cursor-pointer ml-3`" />
    </div>

    <!-- sidebar main menu -->
    <div class="sidebar-menu" :style="menuStyle">
        <Menu class="grid gap-4 px-4">
            <template v-for="(menu, index) in sidebarOptions.mainMenus" :key="index">
                <MenuItem v-if="access(menu.route.name) && menu.visibility" :menuIcon="menu.icon" :route="menu.route"
                    :badgeValue="menu.badge">{{ menu.label }}</MenuItem>
            </template>
        </Menu>
    </div>

    <div class="absolute w-full px-4 bg-white bottom-8">
        <!-- sidebar secondary menu -->
        <Menu class="grid gap-4">
            <template v-for="(menu, index) in sidebarOptions.secondaryMenus" :key="index">
                <MenuItem v-if="access(menu.route.name)" :menuIcon="menu.icon" :route="menu.route">{{ menu.label }}</MenuItem>
            </template>
        </Menu>

        <!-- sidebar return to WordPress link -->
        <a :href="returnToWP" v-if="!isStaff">
            <Button size="md" type="secondary_gray" radius="square" class="w-full my-6"
                :style="`${sidebarOptions.sidebarCollapse ? 'padding:10px !important' : ''}`">
                <div v-if="!sidebarOptions.sidebarCollapse">Return to WordPress</div>
                <template #before>
                    <Icon name="reverseLeft" size="20px" />
                </template>
            </Button>
        </a>

        <!-- sidebar user profile -->
        <div class="pt-6 border-t border-[#EAECF0] flex items-center gap-3 relative"
            :class="[!sidebarOptions.sidebarCollapse && !$settings.isRTL ? 'pl-2 pr-8' : 'pr-2 pl-8']">
            <!-- <img v-if="profile_image_url" :src="profile_image_url" class="rounded-full" :class="`${!sidebarOptions.sidebarCollapse ? 'w-10 h-10' : 'w-12 h-12'}`"> -->
            <!-- <div :style="`background-image: url('${profile_image_url ? profile_image_url : 'https://1.gravatar.com/avatar/729ae85bf62b9917e93538db2f2688ca?s=96&d=mm&r=g'}');`" :class="`${!sidebarOptions.sidebarCollapse ? 'w-10 h-10' : 'w-12 h-12'}`" class="bg-center bg-no-repeat bg-cover rounded-full"></div> -->
            <div>
                <ProfilePicture :name="name" :img="profile_image_url"
                    :class="`${!sidebarOptions.sidebarCollapse ? 'w-10 h-10' : 'w-12 h-12'}`" />
            </div>
            <div v-if="!sidebarOptions.sidebarCollapse" class="w-[calc(100%-52px)]">
                <div class="text-[#344054] text-sm font-semibold capitalize">{{ name }}</div>
                <div class="text-[#475467] text-sm font-normal leading-5 truncate" :title="email">{{ email }}</div>
            </div>
            <!-- <Button class="absolute right-0 text-[#667085] !bg-transparent" v-if="!sidebarOptions.sidebarCollapse" @click.prevent="logout()"> -->
            <Icon name="logOut01" size="20px" :class="[$settings.isRTL ? 'left-0 rotate-180' : 'right-0']"
                class="absolute text-[#667085] cursor-pointer" v-if="!sidebarOptions.sidebarCollapse"
                @click.prevent="logout()" />
            <!-- </Button> -->
        </div>
    </div>
</div>
</template>

<style>
.sidebar-menu::-webkit-scrollbar {
  width: 8px;
}

/* Track */
.sidebar-menu::-webkit-scrollbar-track {
  background: transparent; 
}
 
/* Handle */
.sidebar-menu::-webkit-scrollbar-thumb {
  background: #EAECF0; 
  border-radius: 8px;
}

/* Handle on hover */
.sidebar-menu::-webkit-scrollbar-thumb:hover {
  background: #dadbdf; 
}
</style>